/**
 * Types for EntityUpsertForm.
 * Kept minimal and test-friendly so stories and tests can build props without the full dialog.
 */

import type { ReactNode } from 'react';
import type { SchemaAttribute } from '../../utils/schemaAttributeUtils';

/** Attribute shape accepted by EntityUpsertForm (aligns with SchemaAttribute). */
export type FormAttribute = Pick<
  SchemaAttribute,
  'name' | 'id' | 'value' | 'label' | 'type' | 'required' | 'readOnly' | 'multiValued' | 'isReference'
> & {
  /** Optional allowed values for a select; when set, a Select is used instead of TextInput. */
  allowedValues?: string[];
};

export type FormLayout = 'default' | 'possessive-group';

/** Default form key normalizer (strips "$." prefix). */
export const defaultFormKeyFromAttrName = (n: string): string =>
  (n ?? '').toString().trim().replace(/^\$\./, '') || (n ?? '');

export interface EntityUpsertFormProps {
  /** Attributes to render as form fields (order preserved). */
  schemaAttributes: FormAttribute[];
  /** Current form values keyed by attribute name (or id). */
  formData: Record<string, unknown>;
  /** Called when any field value changes. */
  onFieldChange: (fieldName: string, value: unknown) => void;
  /** When true, show loading spinner instead of fields. */
  loading?: boolean;
  /** Entity type label for empty-state message (e.g. "Sponsored"). */
  entityLabel?: string;
  /** Layout: default (flat list) or possessive-group (one grouped section for named attributes). */
  layout?: FormLayout;
  /** For layout='possessive-group', attribute names (normalized lower) that go into the grouped section. */
  possessiveGroupNames?: string[];
  /** Optional custom field renderer. When not provided, a simple default (TextInput/Select/Checkbox/Textarea/JSON editor) is used. */
  renderField?: (attr: FormAttribute, value: unknown, onChange: (value: unknown) => void) => ReactNode;
  /** Show a debug toolbar (e.g. toggle for field debug info). */
  showFieldDebug?: boolean;
  /** Callback when user toggles field debug. */
  onToggleFieldDebug?: () => void;
  /** When true, all fields are disabled (e.g. save in progress). */
  disabled?: boolean;
  /** Value that indicates an unindexed attribute is not yet loaded (e.g. NON_INDEXED_PROPERTY_STUB). When present and onRequestAttributeValue is set, show "Not loaded*" + Load. */
  nonIndexedStubValue?: string;
  /** Delegate to request a single attribute value (e.g. fetch from API). When user clicks Load for an unindexed field, this is called; parent should update formData with the result. */
  onRequestAttributeValue?: (attributeName: string) => Promise<unknown | null>;
  /** Attribute name currently being loaded (for showing Loader on the Load button). */
  loadingAttribute?: string | null;
  /** When provided, render this instead of the default or possessive-group layout (e.g. PolicyRuleFormLayout). */
  customLayout?: ReactNode;
  /** Entity type (e.g. User, Group, SetTransition). When set, the form uses the dynamic field resolver to pick specialized renderers where applicable. */
  entityType?: string | null;
  /** Normalize attribute name to form key (e.g. strip "$."). Used with entityType for the dynamic resolver. Default strips "$." prefix. */
  formKeyFromAttrName?: (name: string) => string;
  /** When provided, attributes whose name (lowercase) starts with "legacy" are rendered after this heading (e.g. "Legacy" section at bottom). Expect schemaAttributes to have Legacy* already sorted last. */
  legacySectionHeading?: ReactNode;
  /** When provided, per-field validation warning messages are shown inline (icon + tooltip) for the matching attribute. */
  getFieldWarning?: (attr: FormAttribute) => string | null;
}
